import { Button, Col, Row, Skeleton, Tag } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Algorithm, AlgorithmVersion } from '../../types';
import { OverviewLine, OverviewSummary } from '../../types/result';
import { getDataset } from '../../lib/api/dataset-api';
import { Dataset } from '../../types/dataset';
import { DownloadOutlined } from '@ant-design/icons';

type Props = {
  datasetId: string;
  algorithm: Algorithm;
  latestAlgorithmVersion: AlgorithmVersion | undefined;
  previousAlgorithmVersion: AlgorithmVersion | undefined;
  overviewLine: OverviewLine;
};

export const DatasetScoreLineNew = ({
  datasetId,
  algorithm,
  latestAlgorithmVersion,
  previousAlgorithmVersion,
  overviewLine,
}: Props) => {
  const [latestSummary, setLatestSummary] = useState<OverviewSummary | null>();
  const [previousSummary, setPreviousSummary] = useState<OverviewSummary | null>();
  const [meanDifference, setMeanDifference] = useState<number>(0);
  const [perfectDifference, setPerfectDifference] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataset, setDataset] = useState<Dataset | null>(null);

  const loadDataset = async (datasetId: string) => {
    const datasetResult = await getDataset(datasetId);
    setDataset(datasetResult);
  };

  useEffect(() => {
    setLoading(true);
    if (overviewLine) {
      let latest;
      let previous;
      if (overviewLine.latest) {
        latest = overviewLine.latest.value;
        setLatestSummary(latest);
      }
      if (overviewLine.previous) {
        previous = overviewLine.previous.value;
        setPreviousSummary(previous);
      }
      if (previousAlgorithmVersion && latest && previous) {
        setMeanDifference((latest.mean_label_accuracy || 0) - (previous.mean_label_accuracy || 0));
        setPerfectDifference((latest.perfect || 0) - (previous.perfect || 0));
      }

      if (!overviewLine.datasetName) {
        loadDataset(overviewLine.datasetKey);
      }
    }
    setLoading(false);
    return () => {
      setLatestSummary(null);
    };
  }, [overviewLine, previousAlgorithmVersion]);

  const downloadDocumentForUrl = useCallback(
    (presignedUrl: { docType: string; url: string }, version: string) => {
      fetch(presignedUrl.url)
        .then(res => res.blob())
        .then(res => {
          const aElement = document.createElement('a');
          aElement.setAttribute('download', `${algorithm?.name}_${version}_${presignedUrl.docType}`);
          const href = URL.createObjectURL(res);
          aElement.href = href;
          aElement.setAttribute('target', '_blank');
          aElement.click();
          URL.revokeObjectURL(href);
        });
    },
    [algorithm]
  );

  const downloadForVersion = useCallback(
    (summary: OverviewSummary, algorithmVersion: AlgorithmVersion) => {
      if (summary?.presignedUrls?.length) {
        summary?.presignedUrls.forEach(presignedUrl => {
          downloadDocumentForUrl(presignedUrl, algorithmVersion.version);
        });
      }
    },
    [downloadDocumentForUrl]
  );

  const onDownload = useCallback(() => {
    if (latestSummary && latestAlgorithmVersion) {
      downloadForVersion(latestSummary, latestAlgorithmVersion);
    }
    if (previousSummary && previousAlgorithmVersion) {
      downloadForVersion(previousSummary, previousAlgorithmVersion);
    }
  }, [latestSummary, previousSummary, latestAlgorithmVersion, previousAlgorithmVersion, downloadForVersion]);

  return (
    <Row className="container" key={datasetId}>
      {loading && <Skeleton title={false} active paragraph={{ rows: 1, width: '100%' }} />}
      <>
        <Col span={10} className="flex items-center">
          <Link
            className="text"
            style={{ fontSize: '15px', margin: 7 }}
            to={`/overview/${latestAlgorithmVersion?.id}/datasets/${overviewLine.datasetVersion}`}>
            {overviewLine.datasetName || dataset?.name}
          </Link>
        </Col>
        <Col span={6}>
          <p style={{ color: '#6d6e77', fontSize: '16px', margin: 7 }}>
            <span>
              {latestSummary?.mean_label_accuracy
                ? ((latestSummary?.mean_label_accuracy || 0) * 100).toFixed(1) + '%'
                : '-'}
            </span>

            {/* Check if the status is FAILED */}
            {overviewLine.latest?.value.status === 'FAILED' ? (
              <Tag color="red">Failed</Tag>
            ) : (
              previousAlgorithmVersion &&
              latestSummary?.mean_label_accuracy && (
                <Tag style={{ marginLeft: '7px' }} color={meanDifference > 0 ? 'green' : 'orange'}>
                  {meanDifference > 0 ? '+' : ''}
                  {(meanDifference * 100).toFixed(1)}
                </Tag>
              )
            )}
          </p>
        </Col>

        <Col span={6}>
          <p style={{ color: '#6d6e77', fontSize: '16px', margin: 7 }}>
            <span>{latestSummary?.perfect ? ((latestSummary?.perfect || 0) * 100).toFixed(1) + '%' : '-'}</span>

            {/* Check if the status is FAILED */}
            {overviewLine.latest?.value.status === 'FAILED' ? (
              <Tag color="red">Failed</Tag>
            ) : (
              previousAlgorithmVersion &&
              latestSummary?.perfect && (
                <Tag style={{ marginLeft: '7px' }} color={perfectDifference > 0 ? 'green' : 'orange'}>
                  {perfectDifference > 0 ? '+' : ''}
                  {(perfectDifference * 100).toFixed(1)}
                </Tag>
              )
            )}
          </p>
        </Col>

        <Col span={2}>
          {latestSummary || previousSummary ? (
            <Button
              size="small"
              type="text"
              onClick={() => {
                onDownload();
              }}
              icon={<DownloadOutlined />}
            />
          ) : null}
        </Col>
      </>
    </Row>
  );
};
